import { useMemo } from 'react';
import { FAST_USER_INTERACTION_DEBOUNCE } from '../../../../../constants';
import { useDebouncedValue } from '../../../../../hooks/useDebounceValue';
import { useHannibalQuery } from '../../../../../schema/hannibal/HannibalContext';
import {
  PracticeSuggestionsDocument,
  SuggestionPracticeFragment,
  SuggestionPractitionerFragment,
} from '../../../../../schema/hannibal/types.generated';
import { EMPTY_LIST } from '../../../../../util/EMPTY_LIST';
import { SuggestionData } from '../../types';
import { SuggestionList } from '../SuggestionList/SuggestionList';
import {
  CategoryView,
  CategoryLabel,
  ListContainer,
} from '../SuggestionsLayoutTemplate/SuggestionsLayoutTemplate.styles';
import { TabProps } from '../TabProps';

export const mapPracticeSuggestionsToSuggestionData = (
  profiles: readonly SuggestionPracticeFragment[],
): SuggestionData[] =>
  profiles
    .filter((it): it is SuggestionPracticeFragment => it.__typename === 'Practice')
    .map(
      (it): SuggestionData => ({
        type: 'PracticeSuggestion',
        id: `practice-${it.id}`,
        practice: {
          id: it.id,
          name: it.name,
          specialties: it.specialties?.map(it => it.name) ?? [],
          suburb: it.address?.suburb ?? '',
          state: it.address?.state ?? '',
          url: it.url,
        },
      }),
    );

// experimental suggestions
export const mapProfileSuggestionsToSuggestionData = (
  profiles: readonly (SuggestionPracticeFragment | SuggestionPractitionerFragment)[],
): SuggestionData[] =>
  profiles.map(
    (it): SuggestionData =>
      it.__typename === 'Practice'
        ? {
            type: 'PracticeSuggestion',
            id: `practice-${it.id}`,
            practice: {
              id: it.id,
              name: it.name,
              specialties: it.specialties?.map(it => it.name) ?? [],
              suburb: it.address?.suburb ?? '',
              state: it.address?.state ?? '',
              url: it.url,
            },
          }
        : {
            type: 'PractitionerSuggestion',
            id: `practitioner-${it.id}`,
            practitioner: {
              id: it.id,
              title: it.title ?? undefined,
              firstName: it.firstName ?? undefined,
              lastName: it.lastName ?? undefined,
              specialties: it.specialties?.map(specialty => specialty.name) ?? [],
              suburb: it.primaryPractice?.address?.suburb ?? undefined,
              state: it.primaryPractice?.address?.state ?? undefined,
              imagePath: it.profilePhoto?.link ?? undefined,
              url: it.url,
            },
          },
  );

export const PracticeSuggestions = ({
  searchTerm,
  onChoose,
  trackingData,
  toggleSingleSuggestionView,
  showSingleView,
  onFocus,
  listIndex,
}: TabProps) => {
  const query = useDebouncedValue(searchTerm, FAST_USER_INTERACTION_DEBOUNCE);
  const { data } = useHannibalQuery(PracticeSuggestionsDocument, { variables: { query } });

  const profiles = data?.searchText.profiles ?? EMPTY_LIST;

  const practiceSuggestions: SuggestionData[] = useMemo(() => {
    if (showSingleView) {
      return mapPracticeSuggestionsToSuggestionData(profiles);
    }

    return mapPracticeSuggestionsToSuggestionData(profiles).slice(0, 3);
  }, [showSingleView, profiles]);

  const suggestions: SuggestionData[] = practiceSuggestions;

  if (practiceSuggestions.length === 0) {
    return null;
  }

  return (
    <CategoryView>
      {!showSingleView && <CategoryLabel>Practices</CategoryLabel>}

      <ListContainer onFocus={() => onFocus?.(listIndex ?? 0)}>
        <SuggestionList
          suggestions={suggestions}
          onChoose={onChoose}
          trackingData={trackingData}
          searchTerm={searchTerm}
          toggleSingleSuggestionView={toggleSingleSuggestionView}
          showSingleView={showSingleView}
          suggestionType="Practices"
          indexIdentifier={listIndex ?? 0}
        />
      </ListContainer>
    </CategoryView>
  );
};
